import {FC, RefObject, memo, useMemo} from 'react';
import {RoomAudioRenderer, StartAudio} from '@livekit/components-react';
import {
  useLiveKitTracks,
  IUseLiveKitResult,
  useLiveKitAvatar,
  useLiveStreamTracks,
  APP_VARS
} from 'core';
import {LocalAudioTrack} from 'livekit-client';
import {Box, Center} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {BlinkingButton} from 'ui-kit';

import {SettingsButton, VideoRender} from './components';

const TOOLTIP_MUTE_ID = 'VOICE_MODE_MUTE';
const TOOLTIP_UNMUTE_ID = 'VOICE_MODE_UNMUTE';
const TOOLTIP_VIDEO_OFF_ID = 'TOOLTIP_VIDEO_OFF';
const TOOLTIP_VIDEO_ON_ID = 'TOOLTIP_VIDEO_ON';
const TOOLTIP_AVATAR_OFF_ID = 'TOOLTIP_AVATAR_OFF';
const TOOLTIP_AVATAR_ON_ID = 'TOOLTIP_AVATAR_ON';
const TOOLTIP_CLOSE_ID = 'VOICE_MODE_CLOSE';
const TOOLTIP_MCP_ID = 'VOICE_MODE_MCP';

interface IProps {
  cRef: RefObject<HTMLDivElement | null>;
  lk: IUseLiveKitResult;
}

const VoiceControlFC: FC<IProps> = ({lk, cRef}) => {
  const {data: settings, isFetching: isSettingsFetching} = api.settings.useSettingsQuery();
  const mcpBotMutation = api.settings.useUpdateMcpBotSettingsMutation();

  const {micTrackRef, camTrackRef, isMuted, mute, unmute, isCameraEnabled} = useLiveKitTracks();
  const {avatarCamTrackRef, toggleAvatar, isAvatarEnabled, isAvatarSyncing} = useLiveKitAvatar();
  const liveStreamVideos = useLiveStreamTracks();

  const {t} = useT();

  const isMeSpeaking = lk.activeSpeakers.some((i) => !i.isAgent);
  const isAgentSpeaking = lk.activeSpeakers.some((i) => i.isAgent);
  const isSpeaking = isMeSpeaking || isAgentSpeaking;
  const isMcpOn = settings?.mcp_bot.enabled_in_voice;

  const primaryVideo = useMemo(() => {
    if (isCameraEnabled) {
      return camTrackRef;
    } else if (isAvatarEnabled) {
      return avatarCamTrackRef;
    }
  }, [avatarCamTrackRef, camTrackRef, isAvatarEnabled, isCameraEnabled]);

  const secondaryVideo = useMemo(() => {
    return isCameraEnabled && isAvatarEnabled ? avatarCamTrackRef : null;
  }, [avatarCamTrackRef, isAvatarEnabled, isCameraEnabled]);

  const handleMcpStateUpdate = async (newIsOn: boolean) => {
    if (settings?.mcp_bot) {
      await mcpBotMutation.mutateAsync({
        ...settings.mcp_bot,
        enabled_in_voice: newIsOn
      });
    }
  };

  const isAvatarOnly = !isCameraEnabled && isAvatarEnabled;

  return (
    <Box m={'0 -14px'} p={'12px 24px'} bg={'{colors.bgMain}'}>
      <Center gap={'8px'}>
        {/* MICROPHONE MUTE OR UNMUTE */}
        {!isMuted ? (
          <BlinkingButton
            isLoading={lk.isPending}
            tooltipId={TOOLTIP_MUTE_ID}
            blink={lk.isPending ? undefined : isSpeaking ? 'dark' : 'light'}
            icon={isMeSpeaking ? 'MicrophoneActive' : 'Microphone'}
            tooltipLabel={t('voice.microphoneOff')}
            onClick={lk.isConnected ? mute : undefined}
          />
        ) : (
          <BlinkingButton
            icon={'MicrophoneMuted'}
            tooltipId={TOOLTIP_UNMUTE_ID}
            tooltipLabel={t('voice.microphoneOn')}
            onClick={lk.isConnected ? unmute : undefined}
          />
        )}

        {/* CAMERA ON OR OFF */}
        {isCameraEnabled ? (
          <BlinkingButton
            isLoading={lk.isPending}
            blink={'light'}
            icon={'CameraOn'}
            tooltipId={TOOLTIP_VIDEO_OFF_ID}
            tooltipLabel={t('voice.cameraOff')}
            onClick={lk.isConnected ? () => lk.toggleCamera(false) : undefined}
          />
        ) : (
          <BlinkingButton
            isLoading={lk.isPending}
            icon={'CameraOff'}
            tooltipId={TOOLTIP_VIDEO_ON_ID}
            tooltipLabel={t('voice.cameraOn')}
            onClick={lk.isConnected ? () => lk.toggleCamera(true) : undefined}
          />
        )}

        {/* AVATAR ON OR OFF */}
        {APP_VARS.FEATURE_FLAGS.VIDEO_AVATAR && (
          <>
            {isAvatarEnabled ? (
              <BlinkingButton
                isLoading={lk.isPending || isAvatarSyncing}
                blink={'light'}
                icon={'AvatarOn'}
                tooltipId={TOOLTIP_AVATAR_OFF_ID}
                tooltipLabel={t('voice.avatarOff')}
                onClick={lk.isConnected && !isAvatarSyncing ? toggleAvatar : undefined}
              />
            ) : (
              <BlinkingButton
                isLoading={lk.isPending || isAvatarSyncing}
                icon={'AvatarOff'}
                tooltipId={TOOLTIP_AVATAR_ON_ID}
                tooltipLabel={t('voice.avatarOn')}
                onClick={lk.isConnected && !isAvatarSyncing ? toggleAvatar : undefined}
              />
            )}
          </>
        )}

        {/* MCP TOOL ON OR OFF */}
        <BlinkingButton
          isLoading={isSettingsFetching || mcpBotMutation.isPending}
          tooltipId={TOOLTIP_MCP_ID}
          blink={isMcpOn ? 'light' : undefined}
          icon={isMcpOn ? 'McpOn' : 'McpOff'}
          tooltipLabel={isMcpOn ? t('voice.mcpOff') : t('voice.mcpOn')}
          onClick={() => handleMcpStateUpdate(!isMcpOn)}
        />

        {/* AUDIO SETTINGS */}
        <SettingsButton track={micTrackRef.publication?.audioTrack as LocalAudioTrack} />

        {/* CLOSE VOICE MODE */}
        <BlinkingButton
          icon={'Close'}
          tooltipId={TOOLTIP_CLOSE_ID}
          tooltipLabel={t('voice.close')}
          onClick={() => {
            lk.disconnect().then();
          }}
        />

        {/* PLAY INCOMING AUDIO */}
        <RoomAudioRenderer />

        {/* CLICK TO PLAY AUDIO (BROWSER MAY BLOCK PLAYING) */}
        <StartAudio label={'Start Audio'} />
      </Center>

      {(primaryVideo?.publication ||
        secondaryVideo?.publication ||
        liveStreamVideos.length > 0) && (
        <VideoRender
          lk={lk}
          cRef={cRef}
          primaryVideo={primaryVideo}
          secondaryVideo={secondaryVideo}
          liveStreamVideos={liveStreamVideos}
          isAvatarOnly={isAvatarOnly}
        />
      )}
    </Box>
  );
};

export const VoiceControl = memo(VoiceControlFC);
